/**
 * Webhook Log Monitoring Types
 * Shared between client and server for type-safe API contracts
 *
 * Backed by the `webhookLog` table. The table's own `status` column is disused
 * (always NULL in practice), so status is derived from `returnValue`:
 * anything starting with "Error:" is a failure, everything else is a success.
 */

// ============================================================================
// Webhook Log Status
// ============================================================================

/** Derived delivery status for a webhook log entry */
export type WebhookLogStatus = 'ok' | 'error';

/** Quick date-range presets offered in the UI (and accepted by the API) */
export const WEBHOOK_LOG_RANGES = [
  { label: '24h', hours: 24 },
  { label: '48h', hours: 48 },
  { label: '72h', hours: 72 },
  { label: '1 week', hours: 168 },
  { label: '2 weeks', hours: 336 },
  { label: '1 month', hours: 720 },
] as const;

export type WebhookLogRangeLabel = (typeof WEBHOOK_LOG_RANGES)[number]['label'];

// ============================================================================
// Query Parameters
// ============================================================================

export interface WebhookLogsQueryParams {
  /** Filter by client */
  clientID?: string;
  /** Lookback window in hours (default 24, max 720) */
  hours?: string;
  /** Filter by derived status */
  status?: WebhookLogStatus;
  /** Match against the `eventID` embedded in the webhook payload */
  eventID?: string;
  /** Page number (1-based) */
  page?: string;
  /** Results per page */
  limit?: string;
}

// ============================================================================
// Response Types
// ============================================================================

/** A single webhook log row — payloads are truncated to previews */
export interface WebhookLogRow {
  /** Primary key */
  webhookIdx: number;
  /** Client the webhook belongs to */
  clientID: string;
  /** Client name (null when the clientID has no matching client record) */
  clientName: string | null;
  /** Derived status */
  status: WebhookLogStatus;
  /** HTTP status code parsed out of the error message, when present */
  errorCode: number | null;
  /** Short error summary for error rows (e.g. "Request failed with status code 403") */
  errorMessage: string | null;
  /** eventID extracted from the request payload, when present */
  eventID: string | null;
  /** Destination URL the webhook was posted to */
  callback: string | null;
  /** When the webhook fired (ISO string) */
  timeStamp: string | null;
  /** Truncated request payload */
  inputPreview: string | null;
  /** Full character length of the request payload */
  inputLength: number;
  /** Truncated response payload */
  returnPreview: string | null;
  /** Full character length of the response payload */
  returnLength: number;
}

/** Full webhook log record, including untruncated payloads */
export interface WebhookLogDetail extends WebhookLogRow {
  /** Full request payload */
  inputValue: string | null;
  /** Full response payload */
  returnValue: string | null;
  /** Raw value of the disused `status` column */
  rawStatus: string | null;
}

export interface WebhookLogsResponse {
  logs: WebhookLogRow[];
  pagination: {
    page: number;
    limit: number;
    totalCount: number;
    totalPages: number;
  };
  /** Totals for the current filters, ignoring the status filter */
  summary: {
    total: number;
    ok: number;
    error: number;
  };
  cacheInfo?: {
    cachedAt: string | null;
  };
}

export interface WebhookLogDetailResponse {
  log: WebhookLogDetail;
}

// ============================================================================
// Config
// ============================================================================

export const WEBHOOK_LOGS_CONFIG = {
  DEFAULT_PAGE: 1,
  DEFAULT_LIMIT: 50,
  MAX_LIMIT: 100,
  /** Default lookback in hours when none specified */
  DEFAULT_LOOKBACK_HOURS: 24,
  /** Hard ceiling on the lookback window */
  MAX_LOOKBACK_HOURS: 720,
  /** Characters of each payload returned in list responses */
  PREVIEW_LENGTH: 1000,
  /** Cache TTL for list responses (ms) */
  CACHE_TTL_MS: 60_000,
} as const;
